import { ReactNode } from "react";

import {
  DashboardIcon,
  H3Icon,
  LookupIcon,
  MakerIcon,
  MapIcon
} from "@/assets/icons";
import { SPATIAL_TYPE_TO_LAYER_TYPE } from "@/constants/dataset";
import { cn } from "@/lib/utils";
import { type Dataset, type WeightValues } from "@/types/dataset";
import { LayerType } from "@/types/map";
import { calculateWeightPercentage } from "@/utils/weightValues";

import { InputWithClear } from "./InputWithClear";

type SpatialInfo = {
  [key in LayerType]: {
    icon: ReactNode;
    label: string;
    textColor: string;
  };
};

const SPATIAL_INFO: SpatialInfo = {
  [LayerType.ADMINISTRATIVE]: {
    icon: (
      <div className="flex items-center bg-blue-50 rounded-md">
        <MapIcon className="fill-blue-300 w-[12px]" />
      </div>
    ),
    label: "Admin. Area",
    textColor: "text-royal-blue"
  },
  [LayerType.POLYGON]: {
    icon: <DashboardIcon className="fill-tangerine w-[12px]" />,
    label: "Polygon",
    textColor: "text-tangerine"
  },
  [LayerType.POINT]: {
    icon: (
      <div className="flex items-center bg-blue-50 rounded-md p-1">
        <MakerIcon className="fill-blue-300 w-4" width={16} height={16} />
      </div>
    ),
    label: "Point",
    textColor: "text-blue-violet"
  },
  [LayerType.GRID]: {
    icon: <LookupIcon className="fill-teal-500 size-3" />,
    label: "Grid",
    textColor: "text-blue-violet"
  },
  [LayerType.PROFILING]: {
    icon: <LookupIcon className="fill-teal-500 size-3" />,
    label: "Profiling",
    textColor: "text-blue-violet"
  },
  [LayerType.H3]: {
    icon: <H3Icon className="fill-teal-500 w-6 h-6" />,
    label: "Polygon",
    textColor: "text-tangerine"
  },
  [LayerType.GEOHASH]: {
    icon: <DashboardIcon className="fill-tangerine w-[12px]" />,
    label: "Polygon",
    textColor: "text-tangerine"
  },
  [LayerType.LINE]: {
    icon: <DashboardIcon className="fill-purple-300 w-[12px]" />,
    label: "Line",
    textColor: "text-purple-50"
  }
};

// Weight percentage badge component
const WeightPercentage = ({ value = "0%" }: { value?: string }) => {
  return (
    <div
      className="flex flex-row items-center justify-center h-fit bg-[#EEE] text-txtBody rounded-lg px-2 py-1 text-xs font-semibold border border-gainsboro"
      aria-label="weight-percentage"
    >
      {value}
    </div>
  );
};

interface DatasetAttributeItemProps {
  datasetId: string;
  dataset: Dataset;
  isFirst: boolean;
  localWeightValues: WeightValues;
  totalWeightValue: number;
  onUpdateWeight: (
    datasetId: string,
    categoryIdx: string,
    value: string
  ) => void;
  isAllWeightInvalid: boolean;
}

const DatasetAttributeItem = ({
  datasetId,
  dataset,
  isFirst,
  localWeightValues,
  totalWeightValue,
  onUpdateWeight,
  isAllWeightInvalid = false
}: DatasetAttributeItemProps) => {
  if (!dataset) return null;

  const layerType =
    SPATIAL_TYPE_TO_LAYER_TYPE[dataset.data.spatial_aggregation_type];

  return (
    <div className={cn("px-4 gap-3", !isFirst ? "pt-6" : "")}>
      <div className="flex items-center gap-1 mb-4">
        {SPATIAL_INFO[layerType].icon}
        <span className="text-sm text-txtHeading font-semibold">
          {dataset.data.display_name}
        </span>
      </div>
      <div className="flex flex-col gap-3">
        {dataset.categories
          .filter((category) =>
            Object.keys(
              localWeightValues?.[datasetId].attributes || {}
            ).includes(category.id)
          )
          .map((category) => {
            const attributeName =
              dataset.properties.spatialType === "poi"
                ? "Total POI Count" // For POI datasets, only show 1 attribute that can be assigned weight called "Total POI Count"
                : category.attribute_name;

            return (
              <div key={category.id}>
                <div className="text-xs text-txtBody mb-2">{attributeName}</div>
                <div className="grid grid-cols-[1fr_0.35fr] items-center gap-3">
                  <InputWithClear
                    value={
                      localWeightValues?.[datasetId]?.attributes?.[category.id]
                        ?.weight
                    }
                    onChange={(value) =>
                      onUpdateWeight(datasetId, category.id, value)
                    }
                    inputMode="decimal"
                    dataTestId={`weight-input-${datasetId}-${category.id}`}
                    placeholder="Enter weight value"
                    ariaLabel={`Weight value for ${attributeName}`}
                    error={isAllWeightInvalid}
                  />
                  <WeightPercentage
                    value={calculateWeightPercentage(
                      datasetId,
                      category.id,
                      totalWeightValue,
                      localWeightValues
                    )}
                  />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default DatasetAttributeItem;
